import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  v2,
  ApiError,
  type DocumentTemplate,
  type DocumentTemplateDiff,
} from '@/api/v2Client';
import {
  renderTemplatePreview,
  regionOfTemplate,
  lineDiff,
  type DiffLine,
} from '@/lib/templateRender';
import { RESIZE_GRIP_STYLE, useDraggableModal } from '@/hooks/useDraggableModal';
import { DiffLineList } from './DiffLineList';

/**
 * Phase 5.X-v1-parity-polish Feature 3 — field-diff modal between two
 * DocumentTemplates.
 *
 * V1's FormTemplatesScreen had a "Compare fields" modal showing the delta
 * between template versions. V2 doesn't have per-template versioning yet
 * (a separate phase if that lands), so this modal compares any two
 * templates by id. The "no other version" empty state applies when the
 * caller's list has only the current template — handled at the Compare-
 * button level on FormTemplatesPage, not inside the modal.
 */
export interface FieldDiffModalProps {
  readonly left: DocumentTemplate;
  /**
   * Peers the user can pick to compare against — `left` is excluded.
   * Must be non-empty when this modal is opened.
   */
  readonly peers: ReadonlyArray<DocumentTemplate>;
  readonly onClose: () => void;
}

export function FieldDiffModal({ left, peers, onClose }: FieldDiffModalProps): ReactElement {
  const [selectedRightId, setSelectedRightId] = useState<string>(peers[0]?.Id ?? '');
  const [diff, setDiff] = useState<DocumentTemplateDiff | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // phase-ui-draggable-modals — drag the Compare window by its title bar so it
  // can sit beside the templates table. Backdrop / overlay-click / z-index
  // unchanged.
  const drag = useDraggableModal();

  useEffect(() => {
    if (selectedRightId === '') return;
    setLoading(true);
    setError(null);
    setDiff(null);
    v2.documentTemplates
      .diff(left.Id, selectedRightId)
      .then((d) => {
        setDiff(d);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load diff');
      })
      .finally(() => setLoading(false));
  }, [left.Id, selectedRightId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-diff-title"
      data-testid="template-diff-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        ref={drag.windowRef}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          width: '95%',
          maxWidth: 1080,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          ...drag.windowStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          data-testid="template-diff-titlebar"
          {...drag.handleProps}
          style={{
            background: '#1B3A6B',
            padding: '14px 20px',
            color: '#fff',
            ...drag.handleProps.style,
          }}
        >
          <div id="template-diff-title" style={{ fontSize: 16, fontWeight: 700 }}>
            Compare templates
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
            Comparing &ldquo;{left.Name}&rdquo; against another template
          </div>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1, fontSize: 13 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <span style={{ fontWeight: 600 }}>Compare with:</span>
            <select
              value={selectedRightId}
              onChange={(e) => setSelectedRightId(e.target.value)}
              data-testid="template-diff-peer-select"
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                flex: 1,
              }}
            >
              {peers.map((p) => (
                <option key={p.Id} value={p.Id}>
                  {p.Name}
                </option>
              ))}
            </select>
          </label>

          {loading && (
            <div
              data-testid="template-diff-loading"
              style={{ color: 'var(--text-muted)', padding: 16 }}
            >
              Loading diff…
            </div>
          )}

          {error !== null && (
            <div
              role="alert"
              data-testid="template-diff-error"
              style={{
                color: 'var(--red)',
                background: 'var(--red-bg)',
                border: '1px solid #fecaca',
                borderRadius: 6,
                padding: 12,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {diff !== null && !loading && (
            <div data-testid="template-diff-body">
              {diff.changedFields.length === 0 &&
                diff.variables.addedInRight.length === 0 &&
                diff.variables.removedInRight.length === 0 &&
                diff.variables.changed.length === 0 && (
                  <div
                    data-testid="template-diff-identical"
                    style={{
                      padding: 12,
                      marginBottom: 16,
                      background: 'var(--green-bg)',
                      border: '1px solid #bbf7d0',
                      borderRadius: 6,
                      color: 'var(--green)',
                      fontSize: 13,
                    }}
                  >
                    ✓ Templates are identical across all compared fields.
                  </div>
                )}

              {/* Phase phase-form-templates-ui-fixes (FT-3/4) — primary view:
                  the rendered template bodies side by side with line-level
                  differences highlighted. The variable-name/field-name diff
                  moves into the collapsible "Technical details" below. */}
              {(() => {
                const ld = lineDiff(
                  renderTemplatePreview(diff.left.Body),
                  renderTemplatePreview(diff.right.Body),
                );
                return (
                  <div
                    data-testid="template-diff-sidebyside"
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
                  >
                    <DiffColumn template={diff.left} lines={ld.left} side="left" />
                    <DiffColumn template={diff.right} lines={ld.right} side="right" />
                  </div>
                );
              })()}

              {/* Collapsible technical details — kept for admins who need the
                  raw field/variable diff. Always mounted (inside <details>) so
                  it stays addressable; collapsed by default. */}
              <details data-testid="template-diff-technical" style={{ marginTop: 16 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                  Technical details (field &amp; variable changes)
                </summary>
                <div style={{ marginTop: 12 }}>
                  {diff.changedFields.length > 0 && (
                    <section style={{ marginBottom: 16 }}>
                      <div style={sectionLabelStyle}>Changed fields ({diff.changedFields.length})</div>
                      <ul
                        style={{ margin: 0, padding: 0, listStyle: 'none' }}
                        data-testid="template-diff-changed-fields"
                      >
                        {diff.changedFields.map((field) => (
                          <li key={field} style={techRowStyle}>
                            <strong>{field}</strong>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <VariablesSection title="Added in right" rows={diff.variables.addedInRight} testId="diff-added" color="var(--green)" />
                  <VariablesSection title="Removed in right" rows={diff.variables.removedInRight} testId="diff-removed" color="var(--red)" />
                  {diff.variables.changed.length > 0 && (
                    <section style={{ marginBottom: 16 }}>
                      <div style={sectionLabelStyle}>Variables changed ({diff.variables.changed.length})</div>
                      <ul
                        style={{ margin: 0, padding: 0, listStyle: 'none' }}
                        data-testid="diff-variables-changed"
                      >
                        {diff.variables.changed.map((c) => (
                          <li key={c.name} style={techRowStyle}>
                            <strong>{c.name}</strong>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              </details>
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="template-diff-close"
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '6px 16px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Close
          </button>
        </div>

        {/* phase-ui-preview-modal-pdf-height-resize — the shared corner
            resize grip (same mechanism as the Preview modal). */}
        <div
          data-testid="template-diff-resize-grip"
          aria-hidden="true"
          {...drag.resizeHandleProps}
          style={{ ...RESIZE_GRIP_STYLE, ...drag.resizeHandleProps.style }}
        />
      </div>
    </div>
  );
}

const sectionLabelStyle: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 700,
  marginBottom: 8,
};

const techRowStyle: CSSProperties = {
  padding: '6px 0',
  borderBottom: '1px solid var(--border)',
  fontFamily: 'monospace',
  fontSize: 12,
};

interface DiffColumnProps {
  readonly template: DocumentTemplate;
  readonly lines: ReadonlyArray<DiffLine>;
  readonly side: 'left' | 'right';
}

/**
 * One side of the side-by-side comparison: a metadata header (name + module
 * + region) above the rendered body, with changed/added/removed lines
 * highlighted (yellow for changed/added, red strikethrough for removed).
 */
function DiffColumn({ template, lines, side }: DiffColumnProps): ReactElement {
  const region = regionOfTemplate(template);
  const meta = [template.TargetModule, region].filter(Boolean).join(' · ');
  return (
    <div
      data-testid={`template-diff-col-${side}`}
      style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
    >
      <div style={{ padding: '8px 12px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{template.Name}</div>
        {meta && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{meta}</div>}
      </div>
      <DiffLineList lines={lines} />
    </div>
  );
}

interface VariablesSectionProps {
  readonly title: string;
  readonly rows: ReadonlyArray<{ name: string }>;
  readonly testId: string;
  readonly color: string;
}

function VariablesSection({ title, rows, testId, color }: VariablesSectionProps): ReactElement | null {
  if (rows.length === 0) return null;
  return (
    <section style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color,
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {title} ({rows.length})
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }} data-testid={testId}>
        {rows.map((row) => (
          <li
            key={row.name}
            style={{
              padding: '6px 0',
              borderBottom: '1px solid var(--border)',
              fontFamily: 'monospace',
              fontSize: 12,
            }}
          >
            <strong>{row.name}</strong>
          </li>
        ))}
      </ul>
    </section>
  );
}
